import React, { Fragment } from 'react';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

import { ThemeColor } from '../theme';
import { uikitPalette } from '../theme/uikitPalette';
import { HighlightCategory, Highlight } from '../../services/types/Operation';

// ─────────────────────────────────────────────────────────────
// UIKitBadge - Badge individual
// ─────────────────────────────────────────────────────────────
export interface UIKitBadgeProps {
  className?: string;
  color?: ThemeColor;
  variant?: 'standard' | 'dot';
  children?: React.ReactNode;
  style?: React.CSSProperties;
}

const BadgeRoot = styled('span', {
  shouldForwardProp: (prop) => prop !== 'color' && prop !== 'variant',
})<{ color: ThemeColor; variant: 'standard' | 'dot' }>(({ color, variant }) => {
  const palette = uikitPalette[color];

  // dot variant - círculo pequeño sin contenido
  if (variant === 'dot') {
    return {
      display: 'inline-block',
      width: 8,
      height: 8,
      borderRadius: 64,
      backgroundColor: palette.main,
    };
  }

  // standard variant - badge con contenido
  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 26,
    height: 26,
    borderRadius: 64,
    boxSizing: 'border-box' as const,
    fontFamily: "'Open Sans', sans-serif",
    fontSize: 12,
    fontWeight: 400,
    lineHeight: '28px',
    textAlign: 'center' as const,
    backgroundColor: palette.main,
    color: palette.on,
  };
});

export const UIKitBadge: React.FC<UIKitBadgeProps> = ({
  className,
  color = 'primary',
  variant = 'standard',
  children,
  style,
}) => (
  <BadgeRoot color={color} variant={variant} className={className} style={style}>
    {children}
  </BadgeRoot>
);

// ─────────────────────────────────────────────────────────────
// Badge (Highlights) - Grupo de badges con etiquetas
// ─────────────────────────────────────────────────────────────
export interface BadgeProps {
  highlightGroups: HighlightCategory[];
  withoutCategory?: boolean;
}

const Container = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 16,
});

const Category = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
});

const CategoryLabel = styled('span')({
  fontSize: 12,
  fontWeight: 600,
  color: '#67676A',
  marginRight: 8,
});

const HighlightItem = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 6,
});

const HighlightLabel = styled('span')({
  fontSize: 14,
  color: '#67676A',
});

const VLine = styled('div')({
  width: 1,
  height: 24,
  backgroundColor: '#D8D8D9',
});

const mapColorToTheme = (color: string): ThemeColor => {
  const colorMap: Record<string, ThemeColor> = {
    primary: 'primary',
    secondary: 'secondary',
    tertiary: 'tertiary',
    success: 'success',
    error: 'error',
    warning: 'warning',
    red: 'error',
    green: 'success',
    orange: 'warning',
    blue: 'primary',
  };
  return colorMap[color.toLowerCase()] || 'primary';
};

const Badge: React.FC<BadgeProps> = ({ highlightGroups, withoutCategory }) => {
  const { t } = useTranslation();

  const drawHighlight = (group: string, h: Highlight) => (
    <HighlightItem key={`${group}${h.name}`}>
      <UIKitBadge color={mapColorToTheme(h.color)} variant="standard">
        {h.value}
      </UIKitBadge>
      <HighlightLabel>{t(h.name)}</HighlightLabel>
    </HighlightItem>
  );

  const drawGroup = (hg: HighlightCategory, ix: number) => (
    <Fragment key={hg.name}>
      {ix !== 0 && <VLine />}
      <Category>
        {!withoutCategory && <CategoryLabel>{t(hg.name)}</CategoryLabel>}
        {hg.highlights.map((h) => drawHighlight(hg.name, h))}
      </Category>
    </Fragment>
  );

  return <Container>{highlightGroups.map((hg, ix) => drawGroup(hg, ix))}</Container>;
};

export default Badge;
